import React, { useMemo } from "react";
import { Button, Stack, Tooltip, Typography, styled } from "@mui/material";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import { usePageContext } from "../../../context/PageContext";
import { useSessionContext } from "../../../context/SessionContext";
import { PARAMETER_CHANNEL_ID } from "../../../pages/config/settings";
import { useTranslation } from "react-i18next";
import type { CAEGlobalSettings } from "../../../types/CAEGlobalSettings";

interface Props {
    CAEGlobalSettings: CAEGlobalSettings;
    iconColor?: string;
}

const StyledButton = styled(Button)(() => ({
    minWidth: "unset",
    width: "30px",
    height: "30px",
    borderRadius: "5px",
    "&:hover": {
        backgroundColor: "var(--sos-main)",
        "& .MuiSvgIcon-root": {
            color: "var(--background-white)",
        },
    },
}));

const toolTipProps = {
    tooltip: {
        sx: {
            bgcolor: "var(--background-white)",
            color: "var(--sos-main)",
            border: "1px solid var(--sos-main)",
            paddingTop: "0",
            paddingBottom: "0",
        },
    },
};

function stripIbanPrefix(value?: string) {
    return (value ?? "").replace("IBAN", "").trim();
}

async function copyText(text: string, label: string) {
    if (!text) return;
    try {
        if (!navigator.clipboard?.writeText) {
            throw new Error("Clipboard API not available");
        }
        await navigator.clipboard.writeText(text);
    } catch (err) {
        console.error(`Error copying ${label}:`, err);
    }
}

function C051BankData({ CAEGlobalSettings, iconColor = "primary.main" }: Props) {
    const pageContext = usePageContext();
    const session = useSessionContext();
    const { t } = useTranslation();

    const accountSettings = pageContext.page?.accountSettings;
    const marketingSettings = pageContext.page?.marketingSettings;

    const accountTitle =
        accountSettings?.["sos-title"] || CAEGlobalSettings["sos-title"] || "Spendenkonto";

    const recipientName =
        accountSettings?.["sos-recipient-name"] || CAEGlobalSettings["sos-recipient-name"];

    const ibanCopyTooltip =
        accountSettings?.["sos-iban-copy-tooltip"] ||
        CAEGlobalSettings["sos-iban-copy-tooltip"] ||
        t("c050_iban_copy_tooltip");

    const recipientCopyTooltip =
        accountSettings?.["sos-recipient-copy-tooltip"] ||
        CAEGlobalSettings["sos-recipient-copy-tooltip"] ||
        t("c050_recipient_copy_tooltip");

    const bankName = accountSettings?.["sos-bank"] || CAEGlobalSettings["sos-bank"];

    const channelId = session.sessionState[PARAMETER_CHANNEL_ID] as string | undefined;

    const channelIdSettings = marketingSettings?.["channelIDs"] as
        | Record<string, string>
        | undefined;

    const channelAccountMappingSettings = accountSettings?.["channel-account-mapping"] as
        | Record<string, string>
        | undefined;

    const showDynamicIban = Boolean(accountSettings?.["sos-dynamic-iban"]);

    const resolvedIbanForCopy = useMemo(() => {
        const fallback = CAEGlobalSettings["sos-iban"] || "";
        if (!showDynamicIban) return fallback;

        if (!channelId || !channelIdSettings || !channelAccountMappingSettings) return fallback;

        const matchingKey = Object.keys(channelIdSettings).find(
            (key) => channelIdSettings[key] === channelId
        );

        const mappedIban = matchingKey ? channelAccountMappingSettings[matchingKey] : null;
        return mappedIban || fallback;
    }, [
        showDynamicIban,
        channelId,
        channelIdSettings,
        channelAccountMappingSettings,
        CAEGlobalSettings,
    ]);

    const resolvedIbanForDisplay = useMemo(
        () => stripIbanPrefix(resolvedIbanForCopy),
        [resolvedIbanForCopy]
    );

    return (
        <>
            <Typography
                variant="h4"
                sx={{
                    marginTop: 8,
                    marginBottom: 2
                }}>
                {accountTitle}
            </Typography>

            {recipientName ? (
                <Stack
                    direction="row"
                    sx={{
                        alignItems: "center",
                        gap: 1,
                        py: 1
                    }}>
                    <Typography data-cypress="recipientName" variant="body1" sx={{
                        fontSize: 16
                    }}>
                        <b>{t("c050_recipient_label")}</b>
                        {recipientName}
                    </Typography>

                    <Tooltip title={recipientCopyTooltip} placement="top" slotProps={toolTipProps}>
                        <StyledButton
                            data-cypress="recipientNameTooltipButton"
                            aria-label="Empfänger kopieren"
                            onClick={() => copyText(recipientName, "recipient name")}
                        >
                            <ContentCopyIcon fontSize="small" sx={{ color: iconColor }} />
                        </StyledButton>
                    </Tooltip>
                </Stack>
            ) : null}

            <Stack
                direction="row"
                sx={{
                    alignItems: "center",
                    gap: 1,
                    py: 1
                }}>
                <Typography data-cypress="iban" variant="body1" sx={{
                    fontSize: 16
                }}>
                    <b>{t("c050_iban_label")}</b>
                    {resolvedIbanForDisplay}
                </Typography>

                <Tooltip title={ibanCopyTooltip} placement="top" slotProps={toolTipProps}>
                    <StyledButton
                        data-cypress="ibanTooltipButton"
                        aria-label="IBAN kopieren"
                        onClick={() => copyText(resolvedIbanForCopy, "IBAN")}
                    >
                        <ContentCopyIcon fontSize="small" sx={{ color: iconColor }} />
                    </StyledButton>
                </Tooltip>
            </Stack>

            <Stack
                direction="row"
                sx={{
                    alignItems: "center",
                    gap: 1,
                    py: 1
                }}>
                <Typography
                    data-cypress="bankName"
                    variant="body1"
                    sx={{
                        fontSize: 16,
                        p: 0
                    }}>
                    <b>{t("c050_bank_label")}</b>
                    {bankName}
                </Typography>
            </Stack>
        </>
    );
}

export default C051BankData;
